import React, { useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { getSnapshot } from 'mobx-state-tree';
import { useParams, useSearchParams } from 'react-router-dom';

import { useActivityStore, useDomainStore, useNotificationPreferencesStore } from '@/hooks/useStores';
import ActivityFiltersBar from '@/components/activity/ActivityFiltersBar';
import ActivityListItem from '@/components/activity/ActivityListItem';
import ActivityResultsBar from '@/components/activity/ActivityResultsBar';
import FamilyPageHeader from '@/components/layout/FamilyPageHeader';
import EditFamilyNameModal from '@/components/family/EditFamilyNameModal';
import ScreenHelpDot from '@/components/help/ScreenHelpDot';
import ScreenTourLayer from '@/components/help/ScreenTourLayer';
import Spinner from '@/components/shared/Spinner';
import {
  FAMILY_ACTIVITY_TOUR_STEPS,
  FAMILY_ACTIVITY_TOUR_STORAGE_KEY,
  resolveFamilyActivityTourSteps
} from '@/data/family-activity-tour';
import { useScreenTour } from '@/hooks/useScreenTour';
import { type ActivityCategory, type ActivityEvent, type ActivitySortOrder } from '@/types/activity';

import './family-activity-screen.css';

const FamilyActivityScreen: React.FC = observer(() => {
  const { t } = useTranslation('screens');
  const domainStore = useDomainStore();
  const activityStore = useActivityStore();
  const notificationPrefsStore = useNotificationPreferencesStore();
  const { familyId: familyIdParam } = useParams<{ familyId: string }>();
  const [searchParams] = useSearchParams();
  const sentinelRef = useRef<HTMLDivElement>(null);
  const appliedUrlDateFilters = useRef(false);
  const markedSeenForVisit = useRef(false);

  const [showEditNameModal, setShowEditNameModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const family = domainStore.currentFamily;
  const familyDisplayName = family?.displayName ?? family?.name ?? 'Family';
  const filters = activityStore.familyFilters;

  const loadActivityActors = useCallback(async () => {
    if (!familyIdParam) return;
    await activityStore.loadFamilyActivityActors(familyIdParam);
  }, [activityStore, familyIdParam]);

  const loadActivity = useCallback(async () => {
    if (!familyIdParam) return;
    setErrorMessage(null);
    await activityStore.loadFamilyActivity(familyIdParam);
    if (activityStore.error) {
      setErrorMessage(activityStore.error);
    }
  }, [activityStore, familyIdParam]);

  useEffect(() => {
    if (!familyIdParam) {
      setErrorMessage(t('errors.noFamily'));
      return;
    }
    domainStore.setSelectedFamily(familyIdParam);
  }, [domainStore, familyIdParam]);

  // Digest email CTAs deep-link with createdAfter / createdBefore (apply once per mount).
  useEffect(() => {
    if (appliedUrlDateFilters.current) return;
    const createdAfter = searchParams.get('createdAfter')?.trim() ?? '';
    const createdBefore = searchParams.get('createdBefore')?.trim() ?? '';
    if (!createdAfter && !createdBefore) return;
    appliedUrlDateFilters.current = true;
    if (createdAfter) {
      activityStore.setFamilyFilter('createdAfter', createdAfter);
    }
    if (createdBefore) {
      activityStore.setFamilyFilter('createdBefore', createdBefore);
    }
  }, [activityStore, searchParams]);

  useEffect(() => {
    void loadActivityActors();
  }, [loadActivityActors, filters.category, filters.targetType, filters.createdAfter, filters.createdBefore]);

  useEffect(() => {
    void loadActivity();
  }, [
    loadActivity,
    filters.category,
    filters.userId,
    filters.targetType,
    filters.createdAfter,
    filters.createdBefore,
    filters.sortOrder,
    filters.pageSize
  ]);

  // Freeze watermark for row dots, then mark seen (clears AppBar) once per visit.
  useEffect(() => {
    if (!familyIdParam || markedSeenForVisit.current) return;
    if (activityStore.isLoadingFamily) return;
    markedSeenForVisit.current = true;
    void notificationPrefsStore.markActivitySeenOnVisit();
  }, [familyIdParam, activityStore.isLoadingFamily, notificationPrefsStore]);

  useEffect(() => {
    markedSeenForVisit.current = false;
  }, [familyIdParam]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !familyIdParam) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        void (async () => {
          await activityStore.loadMoreFamilyActivity(familyIdParam);
          if (activityStore.error) {
            setErrorMessage(activityStore.error);
          }
        })();
      },
      { rootMargin: '200px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [
    activityStore,
    familyIdParam,
    activityStore.familyItems.length,
    activityStore.hasMoreFamily,
    activityStore.isLoadingMoreFamily
  ]);

  // Derive on each render — MST array refs are stable; useMemo would keep a stale empty list.
  const memberOptions = activityStore.familyActivityActors.map((actor) => ({
    id: actor.userId,
    label: actor.displayName
  }));

  const hasActivity = activityStore.familyTotal > 0;

  const activityTour = useScreenTour({
    steps: FAMILY_ACTIVITY_TOUR_STEPS,
    completedStorageKey: FAMILY_ACTIVITY_TOUR_STORAGE_KEY,
    resolveSteps: (steps) => resolveFamilyActivityTourSteps(steps, hasActivity)
  });

  const handleEditNameSuccess = (displayName: string, alternativeName: string | null) => {
    if (!familyIdParam || !domainStore.currentFamily) return;
    domainStore.updateFamilyMetadata({
      ...domainStore.currentFamily,
      id: familyIdParam,
      displayName,
      alternativeName
    });
    setShowEditNameModal(false);
  };

  if (!familyIdParam) {
    return (
      <div className="activity-page">
        <p className="activity-page__error">{t('errors.noFamily')}</p>
      </div>
    );
  }

  return (
    <div className="activity-page">
      <FamilyPageHeader
        familyDisplayName={familyDisplayName}
        sectionTitle={t('activity.title')}
        onEditFamilyName={() => setShowEditNameModal(true)}
        onStartTour={activityTour.startTour}
        introTourId="activity-intro"
        intro={
          <>
            {t('activity.lede')} <ScreenHelpDot />
          </>
        }
      />

      <div data-tour="activity-filters">
        <ActivityFiltersBar
          category={filters.category as ActivityCategory}
          userId={filters.userId}
          createdAfter={filters.createdAfter}
          createdBefore={filters.createdBefore}
          memberOptions={memberOptions}
          onCategoryChange={(category) => activityStore.setFamilyFilter('category', category)}
          onUserIdChange={(userId) => activityStore.setFamilyFilter('userId', userId)}
          onCreatedAfterChange={(value) => activityStore.setFamilyFilter('createdAfter', value)}
          onCreatedBeforeChange={(value) => activityStore.setFamilyFilter('createdBefore', value)}
        />
      </div>

      <div data-tour="activity-results">
        <ActivityResultsBar
          totalResults={activityStore.familyTotal}
          sortOrder={filters.sortOrder as ActivitySortOrder}
          onSortOrderChange={(order) => activityStore.setFamilyFilter('sortOrder', order)}
        />
      </div>

      {errorMessage && (
        <p className="activity-page__error" role="alert">
          {errorMessage}
        </p>
      )}

      {activityStore.isLoadingFamily ? (
        <div className="activity-page__loading">
          <Spinner />
        </div>
      ) : activityStore.familyItems.length === 0 ? (
        <p className="activity-page__empty">{t('activity.emptyFilters')}</p>
      ) : (
        <>
          <div className="activity-page__list" data-tour="activity-list">
            {activityStore.familyItems.map((event) => {
              const snap = getSnapshot(event) as ActivityEvent;
              return (
                <ActivityListItem
                  key={event.id}
                  event={snap}
                  isNew={notificationPrefsStore.isActivityNew(
                    snap.createdAt,
                    'visit',
                    snap.actorUserId,
                    domainStore.user?.id
                  )}
                />
              );
            })}
          </div>
          {activityStore.hasMoreFamily && <div ref={sentinelRef} className="activity-page__sentinel" aria-hidden />}
          {activityStore.isLoadingMoreFamily && (
            <div className="activity-page__loading activity-page__loading--more">
              <Spinner />
            </div>
          )}
        </>
      )}

      {showEditNameModal && family && (
        <EditFamilyNameModal
          familyId={familyIdParam}
          currentDisplayName={family.displayName}
          canonicalName={family.name}
          hasAlternative={!!family.alternativeName}
          isOpen={showEditNameModal}
          onClose={() => setShowEditNameModal(false)}
          onSuccess={handleEditNameSuccess}
        />
      )}

      <ScreenTourLayer tour={activityTour} />
    </div>
  );
});

export default FamilyActivityScreen;
